import { useToast } from '@airship-platform/airship-patterns';
import {
  FlexBox,
  LoadingButton,
  TabButton,
} from '@airship-platform/jam-web/components';
import { Divider } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link, Outlet, useNavigate, useParams } from 'react-router';
import { useAppointmentProposalOptionModifiers } from '../../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposalOptionSOWs } from '../../../data-access/appointmentProposalOptionSows';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { useProposalTemplates } from '../../../data-access/proposalTemplate';
import { SectionWrapper } from '../../../patterns';
import ROUTE_PATHS from '../../../routes/paths';
import { useApplyProposalTemplateHttp } from '../hooks/useApplyProposalTemplateHttp';

const ProposalTemplateDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const { proposalTemplateId, proposalTemplateOptionId } = useParams<{
    proposalTemplateId: string;
    proposalTemplateOptionId: string;
  }>();

  const { currentProposal, appendSelectedAppointmentProposalOptionsIds } =
    useAppointmentProposals();
  const { fetchAppointmentProposalOptions } = useAppointmentProposalOptions();
  const { appendModifiers } = useAppointmentProposalOptionModifiers();
  const { appendProducts } = useAppointmentProposalOptionProducts();
  const { appendSOWs } = useAppointmentProposalOptionSOWs();
  const { proposalTemplates } = useProposalTemplates();
  const { addTemplateOptions } = useApplyProposalTemplateHttp();

  const proposalTemplate = useMemo(
    () =>
      proposalTemplates.find(
        (t) => t.proposalTemplateId === proposalTemplateId
      ),
    [proposalTemplates, proposalTemplateId]
  );

  if (!proposalTemplate || !currentProposal) {
    return null;
  }

  const onAddTemplate = async () => {
    setIsProcessing(true);

    const { options, products, modifiers, sows } = await addTemplateOptions(
      currentProposal.appointmentId,
      currentProposal.appointmentProposalId,
      {
        proposalTemplateIds: [proposalTemplate.proposalTemplateId],
        optionDefaults: {},
      }
    );

    await Promise.all([
      appendSelectedAppointmentProposalOptionsIds(
        options.map((o) => o.appointmentProposalOptionId)
      ),
      fetchAppointmentProposalOptions(
        currentProposal.appointmentId,
        currentProposal.appointmentProposalId
      ),
    ]);

    appendProducts(products);
    appendModifiers(modifiers);
    appendSOWs(sows);

    showToast(`${proposalTemplate.name} added to proposal`, 'success');

    navigate(
      ROUTE_PATHS.APPOINTMENT_PATH({
        appointmentId: currentProposal.appointmentId,
        appointmentProposalId: currentProposal.appointmentProposalId,
        path: 'options',
        extraPath: options.at(0)?.appointmentProposalOptionId,
      })
    );

    setIsProcessing(false);
  };

  return (
    <SectionWrapper
      maxWidth={'lg'}
      width={'100%'}
      header={proposalTemplate.name}
      alignItems={'flex-start'}
      actionsButtons={{
        left: {
          label: 'Back',
          onClick: () =>
            navigate(
              ROUTE_PATHS.APPOINTMENT_PATH({
                appointmentId: currentProposal.appointmentId,
                appointmentProposalId: currentProposal.appointmentProposalId,
                path: 'options',
                extraPath: 'manage/templates/proposals',
              })
            ),
        },
        right: {
          component: LoadingButton,
          label: 'Add proposal',
          color: 'primary',
          onClick: onAddTemplate,
          loading: isProcessing,
        },
      }}
    >
      <FlexBox flexWrap={'wrap'} gap={1} flex={1}>
        {proposalTemplate.options.map((option) => (
          <TabButton
            component={Link}
            key={option.proposalTemplateOptionId}
            label={`${option.name} (${
              option.products.length + option.modifiers.length
            })`}
            isSelected={
              option.proposalTemplateOptionId === proposalTemplateOptionId
            }
            to={`option/${option.proposalTemplateOptionId}`}
          />
        ))}
      </FlexBox>

      <Divider sx={{ my: 2, visibility: 'hidden' }} />

      <Outlet />
    </SectionWrapper>
  );
};

export default ProposalTemplateDetailPage;
